import React, { useEffect, useState, useContext } from 'react';
import { useApi } from '../../../../hooks/useApi';
import { useNavigate, useParams } from 'react-router-dom';
import {
	useQuoteDispatch,
	useQuoteState,
} from '../../../../stateProviders/QuoteStateProvider';
import { useGlobalState } from '../../../../stateProviders/GlobalStateProvider';
import { LoadingSpinner } from '../../../../globalComponents/LoadingSpinner';
import { PriceDisplay } from './components/PriceDisplay';
import { DeckContext } from '../blocks/context/DeckContext';
import { DeckContext as DeckContextV3 } from '../blocks/context/DeckContextV3';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Portal,
	Radio,
	RadioGroup,
	Typography,
	useTheme,
} from '@mui/material';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { Card } from '../../../../uiFoundationsKit/components/Card';
import ProposalModeMenu from './proposalModeMenu';
import ProposalConfig, {
	ProposalConfig as ProposalConfigType,
	validatePaymentSchedule,
} from './proposalConfig';
import { Proposal } from '../../../../types/api/Proposal';
import { formatReadableDatetime } from '../../../../uiFoundationsKit/core/formatters';
import { QuoteState } from '../../../../types/stateProviders/QuoteStateProvider';
import QuoteStatus from '../../../../enums/quoteStatus';
import verifyProposalConfig from '../blocks/utils/verifyProposalConfig';
import { toastError, toastSuccess } from '../../../../Toaster/Toast.tsx';
import useIsBetaCanvasEnabled from 'src/hooks/useBetaCanvas.ts';

export const Pricing = () => {
	const api = useApi();
	const { quoteId } = useParams();
	const {
		currentQuote,
		selectedOptionIndex,
		options,
		appliedDiscounts,
		materialCost,
		managerDiscount,
	} = useQuoteState();
	const globalState = useGlobalState();
	const quoteDispatch = useQuoteDispatch();
	const isBetaEnabled = useIsBetaCanvasEnabled();

	const { state, pricingContainerRef } = isBetaEnabled
		? useContext(DeckContextV3)
		: useContext(DeckContext);
	const theme = useTheme();

	const [proposal, setProposal] = useState<Proposal>();
	const [isSaving, setIsSaving] = useState(false);
	const [proposalMode, setProposalMode] = useState(false);
	const [showMenu, setShowMenu] = useState(false);
	const [stackMode, setStackMode] = useState(true);
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [pricingOptions, setPricingOptions] = useState<any>(null);
	const [loading, setLoading] = useState(true);
	const [apiError, setApiError] = useState({
		errored: false,
		error: {
			message: '',
		},
		errorType: '',
	});

	const navigate = useNavigate();

	const [configData, setConfigData] = useState<ProposalConfigType>({
		signers: currentQuote?.proposalConfig?.signers || [{ name: '', email: '' }],
		signInPerson: currentQuote?.proposalConfig?.signInPerson || false,
		secondSigner: currentQuote?.proposalConfig?.secondSigner || false,
		includePaymentSchedule: true,
		includeCustomNotes:
			currentQuote?.proposalConfig?.includeCustomNotes || false,
		paymentSchedule: currentQuote?.proposalConfig?.paymentSchedule,
		notes: currentQuote?.proposalConfig?.notes,
	});

	useEffect(() => {
		if (!selectedOptionIndex && options?.length) {
			quoteDispatch({
				selectedOptionIndex: 0,
			});
		}
	}, [selectedOptionIndex, options]);

	useEffect(() => {
		if (!quoteId) return;

		// Single API call to fetch all pricing options on component mount or relevant state changes
		(async () => {
			const cleanedOptions: QuoteState['options'] = options.filter((option) => {
				return option.deckingType && option.deckingType.length;
			});

			let response;

			if (isBetaEnabled) {
				response = await api.calculatePricingV3.post(
					quoteId,
					cleanedOptions, // Pass all options from currentQuote
					state.blocks
				);
			} else {
				response = await api.calculatePricing.post(
					quoteId,
					cleanedOptions, // Pass all options from currentQuote
					state.blocks
				);
			}

			api.handleEvent.quotePriced(quoteId);

			if (!response.error) {
				setPricingOptions(response);
				setLoading(false);

				if (currentQuote?.status == QuoteStatus.STARTED) {
					api.quotations.updateQuotation(quoteId, {
						status: QuoteStatus.COMPLETED,
					});
				}
			} else {
				setApiError(response);
				setLoading(false);
				setShowErrorModal(true);
			}

			const existingProposal = await api.proposal.find({
				quoteId,
			});

			if (existingProposal) {
				setProposal(existingProposal);
			}
		})();
	}, [quoteId, currentQuote]);

	const saveSelection = async () => {
		if (proposalMode) {
			const error = verifyProposalConfig(configData, totalDiscountedPrice);

			if (error) {
				toastError({
					title: 'Error saving selection',
					description: error,
				});

				return false;
			}
		}

		if (quoteId && validateOption()) {
			setIsSaving(true);

			const quoteResult = await updateQuote();

			if (!quoteResult.errored) {
				api.handleEvent.quoteSaved(quoteId);

				const snapshotResult = await api.snapshot.createSnapshot(quoteId);

				if (snapshotResult.errored) {
					toastError({
						title: 'Error saving selection',
						description: snapshotResult.error.message,
					});
				} else {
					toastSuccess({
						title: 'Selection Saved',
						description: 'You have successfully saved your pricing selection.',
					});

					setIsSaving(false);
					return true;
				}
			} else {
				toastError({
					title: 'Error saving selection',
					description: quoteResult.error.message,
				});
			}

			setIsSaving(false);
		}
	};

	const validateOption = () => {
		const option = currentQuote?.options?.[selectedOptionIndex || 0];

		if (
			option?.deckingType?.trim().length &&
			option?.railingType?.trim().length &&
			(option?.railingType !== 'IRX' || option?.railCapStyle?.trim().length)
		) {
			return true;
		} else {
			toastError({
				title: 'Validation Error',
				description: 'Selected option is incomplete',
			});

			return false;
		}
	};

	const updateQuote = async () => {
		if (quoteId) {
			const result = await api.quotations.updateQuotation(quoteId, {
				...(proposalMode ? { proposalConfig: configData } : {}),
				materialCost: materialCost,
				selectedOptionIndex: selectedOptionIndex,
				appliedDiscounts: appliedDiscounts,
				managerDiscount: managerDiscount,
				...(selectedOptionIndex && {
					topLinePrice:
						pricingOptions['option' + (selectedOptionIndex + 1)]
							.totalClientCost,
				}),
			});

			return result;
		}
	};

	const option = options[selectedOptionIndex || 0];
	const pricingOption = pricingOptions
		? pricingOptions[Object.keys(pricingOptions)[selectedOptionIndex || 0]]
		: [];

	const optionComplete =
		Boolean(option.deckingType) &&
		Boolean(option.railingType) &&
		(option.railingType !== 'IRX' || Boolean(option.railCapStyle));

	let totalDiscountedPrice = pricingOption?.totalClientCost;

	appliedDiscounts.forEach((discount) => {
		totalDiscountedPrice = Math.round(
			totalDiscountedPrice * (1 - discount.discountPercentage / 100)
		);
	});

	if (managerDiscount) {
		totalDiscountedPrice = Math.round(
			totalDiscountedPrice * (1 - managerDiscount.percentage / 100)
		);
	}

	if (loading) {
		return (
			<div className="flex-1 flex items-center justify-center">
				<LoadingSpinner />
			</div>
		);
	}

	if (showErrorModal) {
		return (
			<Dialog open={apiError.errored}>
				<DialogTitle sx={{ backgroundColor: 'white' }}>
					Error calculating price
				</DialogTitle>

				<DialogContent sx={{ backgroundColor: 'white' }}>
					{apiError.errorType == 'Deck validation' && apiError.error.message}
					{apiError.errorType != 'Deck validation' &&
						'Price is unavailable—try again.'}
				</DialogContent>

				<DialogActions sx={{ backgroundColor: 'white' }}>
					<Button
						onClick={() => setShowErrorModal(false)}
						sx={{
							backgroundColor: 'primary.main',
							color: 'white',
							':hover': { backgroundColor: 'primary.main', color: 'white' },
						}}
					>
						Close
					</Button>
				</DialogActions>
			</Dialog>
		);
	}

	if (!apiError.errored)
		return (
			<Box
				sx={{
					width: '100%',
					height: '100%',
				}}
			>
				<Portal container={pricingContainerRef?.current ?? undefined}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: theme.spacing(2),
						}}
					>
						<Box sx={{ position: 'relative' }}>
							<Button
								sx={{
									border: `1px solid ${theme.palette.primary[500]}`,
									borderRadius: theme.shape.borderRadius,
									px: theme.spacing(2),
									py: theme.spacing(1),
								}}
								onClick={() => {
									setShowMenu((prev) => !prev);
								}}
							>
								<MoreHorizIcon />
							</Button>

							{showMenu && quoteId && (
								<ProposalModeMenu
									quoteId={quoteId}
									pricingOption={pricingOption}
									optionComplete={optionComplete}
									proposalMode={proposalMode}
									setProposalMode={setProposalMode}
									setShowMenu={setShowMenu}
									stackMode={stackMode}
									setStackMode={setStackMode}
								/>
							)}
						</Box>

						<Button
							sx={{
								color: theme.palette.primary.main,
								border: `1px solid ${theme.palette.primary.main}`,
								borderRadius: theme.shape.borderRadius,
							}}
							endIcon={
								<BookmarkBorderOutlinedIcon
									sx={{ color: theme.palette.primary.main }}
								/>
							}
							onClick={saveSelection}
							disabled={isSaving}
						>
							{isSaving ? 'Saving ...' : 'Save'}
						</Button>

						{proposalMode && (
							<Button
								sx={{
									backgroundColor: theme.palette.primary.main,
									color: theme.palette.common.white,
									borderRadius: theme.shape.borderRadius,
								}}
								endIcon={
									<Box
										sx={{
											width: 24,
											height: 24,
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
										}}
									>
										{isSaving ? (
											<LoadingSpinner className="w-[24px] h-[24px]" />
										) : (
											<RemoveRedEyeOutlinedIcon />
										)}
									</Box>
								}
								disabled={isSaving}
								onClick={async () => {
									// Validate payment schedule before allowing preview
									if (configData.includePaymentSchedule) {
										const validationError = validatePaymentSchedule(
											configData,
											totalDiscountedPrice
										);
										if (validationError) {
											toastError({
												title: 'Validation Error',
												description: validationError,
											});
											return;
										}
									}
									if (await saveSelection())
										navigate(`/quotations/${quoteId}/preview`);
								}}
							>
								Preview Proposal
							</Button>
						)}
					</Box>
				</Portal>

				<Box
					sx={{
						width: '100%',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Box>
						<Typography
							sx={{
								fontWeight: theme.typography.fontWeightBold,
								color: theme.palette.text.primary,
							}}
							component={'div'}
						>
							Pricing
						</Typography>
						{currentQuote?.updatedAt && (
							<Typography
								sx={{
									color: theme.palette.text.secondary,
									fontSize: theme.typography.xs,
								}}
							>
								Last Price Saved on{' '}
								{formatReadableDatetime(currentQuote.updatedAt)}
							</Typography>
						)}
					</Box>

					{proposal && (
						<Typography
							sx={{
								color: theme.palette.text.secondary,
								fontSize: theme.typography.xs,
							}}
						>
							Last Sent on {formatReadableDatetime(proposal?.createdAt)}
						</Typography>
					)}
				</Box>

				<RadioGroup
					value={selectedOptionIndex}
					onChange={(e) => {
						quoteDispatch({ selectedOptionIndex: parseInt(e.target.value) });
					}}
					sx={{ width: proposalMode ? '57%' : '100%', marginTop: '15px' }}
				>
					<Box
						sx={{
							width: '100%',
							display: 'flex',
							justifyContent: 'start',
							alignItems: 'center',
							gap: '15px',
						}}
					>
						{options?.map((option, idx) => {
							const isSelected = selectedOptionIndex === idx;
							const cardBg = isSelected
								? theme.palette.primary.main
								: theme.palette.background.paper;
							const cardColor = isSelected
								? theme.palette.background.paper
								: theme.palette.text.primary;

							return (
								<Card
									key={idx}
									onClick={() => quoteDispatch({ selectedOptionIndex: idx })}
									elevation={isSelected ? 3 : 1}
									sx={{
										width: 'calc((100% / 3) - 10px)',
										minHeight: '80px',
										display: 'flex',
										flexDirection: 'column',
										justifyContent: 'center',
										alignItems: 'flex-start',
										padding: '10px',
										cursor: 'pointer',
										bgcolor: cardBg,
										color: cardColor,
										transition: theme.transitions.create(
											['background-color', 'box-shadow'],
											{
												duration: theme.transitions.duration.short,
											}
										),
										borderRadius: '8px',
										border: isSelected
											? `2px solid ${theme.palette.primary.main}`
											: `1px solid ${theme.palette.divider}`,
									}}
								>
									<FormControlLabel
										value={idx}
										control={
											<Radio
												sx={{
													'&.Mui-checked': {
														color: theme.palette.background.paper,
													},
												}}
											/>
										}
										label={`Option ${idx + 1}`}
										sx={{
											width: '100%',
											'& .MuiFormControlLabel-label': {
												fontWeight: 600,
												color: isSelected
													? theme.palette.background.paper
													: theme.palette.text.primary,
											},
										}}
									/>

									<Typography
										sx={{
											width: '100%',
											paddingLeft: '30px',
											fontSize: '12px',
											color: isSelected
												? theme.palette.background.paper
												: theme.palette.text.secondary,
										}}
									>
										{option.deckingType || '---'}
									</Typography>

									<Typography
										sx={{
											width: '100%',
											paddingLeft: '30px',
											fontSize: '12px',
											color: isSelected
												? theme.palette.background.paper
												: theme.palette.text.secondary,
										}}
									>
										{option.railingType || '---'}{' '}
										{option.railCapStyle ? `, ${option.railCapStyle}` : ''}
									</Typography>
								</Card>
							);
						})}
					</Box>
				</RadioGroup>

				<Box sx={{ width: '57%', marginTop: '20px' }}>
					{!optionComplete && (
						<Box>
							<Typography sx={{ textDecoration: 'italic', opacity: 0.7 }}>
								Selections Incomplete
							</Typography>
						</Box>
					)}

					{optionComplete && (
						<Box
							sx={{
								width: '100%',
								display: 'flex',
								flexDirection: 'column',
								gap: '10px',
							}}
						>
							<Box>
								<Typography>Decking: {option.deckingType}</Typography>
								<Typography>Railing: {option.railingType}</Typography>

								{option.railCapStyle && (
									<Typography>Rail Cap: {option.railCapStyle}</Typography>
								)}
							</Box>
							<Box sx={{ width: '100%' }}>
								<Typography
									sx={{ width: '100%' }}
									className="flex flex-wrap gap-[10px]"
								>
									Price:
									<PriceDisplay price={pricingOption?.totalClientCost || 0} />
								</Typography>

								<Typography>Payment Plans:</Typography>

								<ul className="pl-[20px] w-[100%] overflow-x-auto">
									{globalState.company?.financingPlans.map((plan) => {
										return (
											<li
												className="flex gap-[10px] w-[100%]"
												key={plan.planName}
											>
												{plan.planName}:
												<PriceDisplay
													price={
														(pricingOption?.totalClientCost || 0) *
														(plan.paymentFactor / 100)
													}
												/>
												/ month
											</li>
										);
									})}
								</ul>
							</Box>
						</Box>
					)}
				</Box>

				{proposalMode && (
					<ProposalConfig
						quoteId={quoteId}
						totalCost={totalDiscountedPrice}
						configData={configData}
						setConfigData={setConfigData}
					/>
				)}
			</Box>
		);
};
